"use client";

import { size, theme } from "@/config/theme";
import { SCButton, SCContainerContent } from "@/utils/styledComponents";
import styled from "styled-components";
import Image from "next/image";
import { breakpoints } from "@/utils/breakpoint";

type ContentBlockWithImageLeftProps = {
    imageSrc: any;
    altText: string;
    title: string;
    paragraph: React.ReactNode;
    points: { title: string; description: string }[];
    buttonText: string;
    buttonUrl: string;
    priority?: boolean;
    sizes?: string;
};

const ContentBlockWithImageLeft = ({
    imageSrc,
    altText,
    title,
    paragraph,
    points,
    buttonText,
    buttonUrl,
    priority = false,
    sizes
}: ContentBlockWithImageLeftProps) => {
    return (
        <SCWrapper theme={theme}>
            <SCContent theme={theme}>
                <div className="image-container">
                    <Image
                        src={imageSrc}
                        alt={altText}
                        priority={priority}
                        sizes={sizes}
                    />
                </div>

                <div className="text-container">
                    <h2 className="text-largexlarge">{title}</h2>
                    <div className="text-medium">{paragraph}</div>

                    {points.map((point, index) => (
                        <div className="text-small" key={index}>
                            <h3>{point.title}</h3> {point.description}
                        </div>
                    ))}

                    <SCBottomButton
                        theme={theme}
                        onClick={() => window.open(buttonUrl, "_blank")}
                    >
                        {buttonText}
                    </SCBottomButton>
                </div>
            </SCContent>
        </SCWrapper>
    );
};

export default ContentBlockWithImageLeft;

const SCWrapper = styled.div`
    background-color: ${(props) => (props.theme as any)["color-class-blue"]};
    display: flex;
    justify-content: center;
    padding: ${(props) => (props.theme as any)["spacing-lxl"]} 0;
`;

const SCContent = styled(SCContainerContent)`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;

    ${breakpoints(
        "flex-direction",
        "",
        [{ [size.px.sxs]: "column-reverse" }],
        "max-width"
    )};

    > div {
        width: 50%;
        ${breakpoints("width", "", [{ [size.px.sxs]: "100%" }], "max-width")};
    }

    .text-container {
        .text-xlarge {
            font-size: ${(props) => (props.theme as any)["fontsize-xl"]};
            margin-bottom: ${(props) => (props.theme as any)["spacing-l"]};
        }

        .text-largexlarge {
            font-size: ${(props) => (props.theme as any)["fontsize-lxl"]};
            font-weight: 500;
            margin-bottom: ${(props) => (props.theme as any)["spacing-m"]};
        }

        .text-medium {
            font-size: ${(props) => (props.theme as any)["fontsize-m"]};
            color: ${(props) => (props.theme as any)["color-grey"]};
            margin-bottom: ${(props) => (props.theme as any)["spacing-l"]};
        }

        .text-small {
            font-size: ${(props) => (props.theme as any)["fontsize-s"]};
            color: ${(props) => (props.theme as any)["color-grey"]};
            margin-bottom: ${(props) => (props.theme as any)["spacing-l"]};
        }
    }

    .image-container {
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            width: 80%;
            min-width: 400px;
            height: auto;
            ${breakpoints(
                "width",
                "",
                [{ [size.px.sxs]: "60%" }],
                "max-width"
            )};
        }
    }
`;

const SCBottomButton = styled(SCButton)`
    background-color: ${(props) => (props.theme as any)["color-blue"]};
    color: ${(props) => (props.theme as any)["color-white"]};
    border: none;
    margin-top: ${(props) => (props.theme as any)["spacing-l"]};
`;
